import { RightOutlined, WarningFilled } from '@ant-design/icons';
import { Badge } from 'components/ui';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import dayjs from 'dayjs';
import { CycleStats, getOvulationAndFertilityWindow } from 'hooks/useSymptomTrackingData';
import { cn } from 'lib/utils';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { routes } from 'services/RouteService';

interface CycleDotGraphProps {
  cycle: CycleStats;
  cycleStats: { average: number; standardDeviation: number; maxVariationFromAverage: number };
  clickable?: boolean;
}

// A light neutral, not `text.disabled` — this is a decorative "empty" dot,
// not text, and read too dark against the others.
const DOT = 'dot box-border inline-block size-2 rounded-full bg-chart-grid';
const DOT_FILLED = 'dotFilled bg-chart-severity-very-bad';
const DOT_OVULATION = 'dotOvulation bg-inverse';
// A lighter step than ovulation's, so the fertile window reads as visually
// distinct from it instead of both looking like the same dark blue.
const DOT_FERTILE = 'dotFertile bg-chart-4';
const DOT_OVULATION_OUTLINE = 'dotOvulationOutline border-2 border-inverse bg-transparent';
const DOT_FERTILE_OUTLINE = 'dotFertileOutline border-2 border-chart-4 bg-transparent';
const DOT_TODAY_RING =
  'dotTodayRing box-border flex size-3.5 shrink-0 items-center justify-center rounded-full border-2 border-muted-foreground';

// Each entry of the details dialog is a title over its value, with room between the entries
const CYCLE_DETAIL_TITLE = 'mt-5 mb-0.5 font-normal text-subtle-foreground first:mt-0';
const CYCLE_DETAIL_VALUE = 'm-0';
const LEGEND_DOT = 'mr-1 inline-block size-2 rounded-full';

const CycleDotGraph: React.FC<CycleDotGraphProps> = ({ cycle, cycleStats, clickable = true }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();

  // Use shared utility for ovulation/fertility window and modal details
  const {
    ovulationIdx,
    fertileStartIdx,
    fertileEndIdx,
    ovulationDate,
    fertileStartDate,
    fertileEndDate,
    isFertilityWindowPredictionReliable,
  } = getOvulationAndFertilityWindow(cycle, cycleStats);

  // Use indices and dates directly for rendering dots and modal details
  const startDate = cycle.start;
  const periodLength = cycle.periodLength;

  // Status of today (the last dot of an in-progress cycle), mirroring the dot
  // colors below and the labels shown at the top of the tracking survey
  const todayIdx = cycle.totalLength - 1;
  let todayDotClass = DOT;
  let todayStatusLabel: string | null = null;
  if (!cycle.completed) {
    if (todayIdx < periodLength) {
      todayDotClass = cn(DOT, DOT_FILLED);
      todayStatusLabel = t('symptomTracking.cycles.legend.period');
    } else if (isFertilityWindowPredictionReliable && ovulationIdx !== null && todayIdx === ovulationIdx) {
      todayDotClass = cn(DOT, DOT_OVULATION_OUTLINE);
      todayStatusLabel = t('symptomTracking.survey.fertility.possibleOvulationDay');
    } else if (
      isFertilityWindowPredictionReliable &&
      fertileStartIdx !== null &&
      fertileEndIdx !== null &&
      todayIdx >= fertileStartIdx &&
      todayIdx <= fertileEndIdx
    ) {
      todayDotClass = cn(DOT, DOT_FERTILE_OUTLINE);
      todayStatusLabel = t('symptomTracking.survey.fertility.possibleFertilityDay');
    }
  }

  return (
    <>
      <div
        className="flex items-center justify-between gap-2 [&_.anticon]:text-subtle-foreground"
        onClick={() => clickable && setModalOpen(true)}
        style={{ cursor: clickable ? 'pointer' : 'default' }}
      >
        <div className="mb-3 flex w-fit max-w-full flex-col overflow-x-hidden text-base">
          <div className="flex w-full justify-between font-medium">
            {cycle.start.format('LL').replace(dayjs().year().toString(), '').trim()}
            {!cycle.completed ? (
              <span className="text-2xs">
                <Badge variant="info" className="ml-2">
                  <Trans
                    i18nKey="symptomTracking.cycles.inProgress"
                    count={cycle.totalLength}
                    components={{ sup: <sup /> }}
                  />
                </Badge>
              </span>
            ) : (
              <span className="ml-2 text-sm font-medium text-subtle-foreground">
                {cycle.totalLength} {t('symptomTracking.nextPeriod.days')}
              </span>
            )}
          </div>
          <div className="flex w-fit items-center gap-0.5">
            {cycle.completed
              ? Array.from({ length: cycle.totalLength }).map((_, i) => {
                  let dotClass = DOT;
                  if (isFertilityWindowPredictionReliable && i === ovulationIdx) {
                    dotClass = cn(DOT, DOT_OVULATION);
                  } else if (
                    isFertilityWindowPredictionReliable &&
                    fertileStartIdx !== null &&
                    fertileEndIdx !== null &&
                    i >= fertileStartIdx &&
                    i <= fertileEndIdx
                  ) {
                    dotClass = cn(DOT, DOT_FERTILE);
                  } else if (i < periodLength) {
                    dotClass = cn(DOT, DOT_FILLED);
                  }
                  return <span key={i} className={dotClass} />;
                })
              : Array.from({ length: cycle.totalLength }).map((_, i) => {
                  // The last dot of the current cycle is today: ring it so it
                  // isn't confused with the ovulation dot
                  if (i === todayIdx) {
                    return (
                      <span
                        key={i}
                        className={DOT_TODAY_RING}
                        title={t('symptomTracking.cycleDetail.today')}
                        data-testid="cycle-dot-today"
                      >
                        <span className={todayDotClass} />
                      </span>
                    );
                  }
                  let dotClass = DOT;
                  // Predict ovulation/fertility window based on average
                  if (i < periodLength) {
                    dotClass = cn(DOT, DOT_FILLED);
                  } else {
                    if (isFertilityWindowPredictionReliable && ovulationIdx !== null && i === ovulationIdx) {
                      dotClass = cn(DOT, DOT_OVULATION_OUTLINE);
                    } else if (
                      isFertilityWindowPredictionReliable &&
                      fertileStartIdx !== null &&
                      fertileEndIdx !== null &&
                      i >= fertileStartIdx &&
                      i <= fertileEndIdx
                    ) {
                      dotClass = cn(DOT, DOT_FERTILE_OUTLINE);
                    }
                  }
                  return <span key={i} className={dotClass} />;
                })}
          </div>
        </div>
        {clickable && <RightOutlined />}
      </div>
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle asChild>
              <h3>{t('symptomTracking.cycles.cycleDetails')}</h3>
            </DialogTitle>
          </DialogHeader>
          <div>
            <h4 className={CYCLE_DETAIL_TITLE}>{t('symptomTracking.cycleDetail.dates')}</h4>

            <p className={CYCLE_DETAIL_VALUE}>
              <b>
                {!cycle.completed ? (
                  <Trans
                    i18nKey="symptomTracking.cycleDetail.datesInProgress"
                    values={{ startDate: startDate.format('LL').replace(dayjs().year().toString(), '').trim() }}
                  />
                ) : (
                  `${startDate.format('LL')} - ${cycle.start.add(cycle.totalLength - 1, 'day').format('LL')}`
                )}
              </b>
            </p>
            <h4 className={CYCLE_DETAIL_TITLE}>{t('symptomTracking.cycleDetail.duration')}</h4>
            <p className={CYCLE_DETAIL_VALUE}>
              <b>
                {!cycle.completed ? (
                  <Trans i18nKey="symptomTracking.cycleDetail.durationInProgress" />
                ) : (
                  <Trans i18nKey="symptomTracking.cycleDetail.durationDays" count={cycle.totalLength} />
                )}
              </b>
            </p>
            {!cycle.completed && (
              <>
                <h4 className={CYCLE_DETAIL_TITLE}>
                  <span className={cn(DOT_TODAY_RING, 'mr-1 inline-flex align-middle')}>
                    <span className={todayDotClass} />
                  </span>
                  {t('symptomTracking.cycleDetail.today')}
                </h4>
                <p className={CYCLE_DETAIL_VALUE}>
                  <b>{dayjs().format('LL')}</b>
                  {todayStatusLabel && (
                    <>
                      <br />
                      {todayStatusLabel}
                    </>
                  )}
                </p>
              </>
            )}
            <h4 className={CYCLE_DETAIL_TITLE}>
              <span className={cn(DOT_FILLED, LEGEND_DOT)} />
              {t('symptomTracking.cycleDetail.periodDuration')}
            </h4>
            <p className={CYCLE_DETAIL_VALUE}>
              <b>
                <Trans i18nKey="symptomTracking.cycleDetail.durationDays" count={periodLength} />
              </b>
            </p>
            {isFertilityWindowPredictionReliable ? (
              <>
                <h4 className={CYCLE_DETAIL_TITLE}>
                  <span className={cn(DOT_FERTILE, LEGEND_DOT)} />
                  {t('symptomTracking.cycleDetail.probableFertilityPeriod')}
                </h4>
                <p className={CYCLE_DETAIL_VALUE}>
                  <Trans
                    i18nKey="symptomTracking.cycleDetail.probableFertilityPeriodDay"
                    values={{ start: fertileStartDate.format('dddd LL'), end: fertileEndDate.format('dddd LL') }}
                    components={{ b: <b /> }}
                  />
                </p>
                <h4 className={CYCLE_DETAIL_TITLE}>
                  <span className={cn(DOT_OVULATION, LEGEND_DOT)} />
                  {t('symptomTracking.cycleDetail.probableOvulationDay')}
                </h4>
                <p className={CYCLE_DETAIL_VALUE}>
                  <b>{ovulationDate.format('LL')}</b>
                </p>
                <Alert variant="warning" className="mt-6">
                  <WarningFilled className="translate-y-0.5 text-chart-severity-bad" />
                  <AlertDescription>{t('symptomTracking.cycles.fertilityWarning')}</AlertDescription>
                </Alert>
              </>
            ) : (
              <>
                <h4 className={CYCLE_DETAIL_TITLE}>
                  <span className={cn(DOT_FERTILE, LEGEND_DOT)} />
                  {t('symptomTracking.cycleDetail.probableFertilityPeriod')}
                </h4>
                <Alert variant="warning" className="mt-1">
                  <WarningFilled className="translate-y-0.5 text-chart-severity-bad" />
                  <AlertDescription>
                    <Trans
                      i18nKey="symptomTracking.cycleDetail.cyclesNotRegularEnough"
                      components={{ a: <Link to={routes.messagingHome.route} /> }}
                    />
                  </AlertDescription>
                </Alert>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Example component for demonstration purposes

export const CycleDotGraphExample: React.FC<{ showInProgress?: boolean }> = ({ showInProgress = false }) => {
  // Simulate a 28-day cycle with a 5-day period
  const mockCycle = {
    start: dayjs().subtract(28, 'day'),
    periodLength: 5,
    totalLength: showInProgress ? 22 : 28,
    completed: !showInProgress,
  } as any; // Type assertion for brevity

  // Simulate average stats
  const mockCycleStats = {
    average: 28,
    maxVariationFromAverage: 2,
    standardDeviation: 2,
  };

  // Minimal styles for demonstration

  return <CycleDotGraph cycle={mockCycle} cycleStats={mockCycleStats} clickable={false} />;
};

export default CycleDotGraph;
